<template>
  <v-card
    class="site-card"
    hover
    @click="$emit('click')"
  >
    <!-- Mosaico: un lugar se muestra con varias fotos a la vez, no con una
         sola foto de catalogo como los productos -->
    <div
      v-if="imagenes.length > 0"
      class="mosaico"
      :class="`mosaico--${Math.min(imagenes.length, 3)}`"
    >
      <div class="celda principal">
        <v-img
          :src="imagenes[0]"
          cover
          class="foto"
        />
      </div>

      <div
        v-for="(imagen, indice) in secundarias"
        :key="indice"
        class="celda"
      >
        <v-img
          :src="imagen"
          cover
          class="foto"
        />
        <div
          v-if="indice === secundarias.length - 1 && fotosExtra > 0"
          class="mas-fotos"
        >
          +{{ fotosExtra }}
        </div>
      </div>
    </div>

    <div
      v-else
      class="sin-imagen"
    >
      <v-icon
        size="40"
        color="grey-lighten-2"
      >
        mdi-image-off-outline
      </v-icon>
    </div>

    <!-- Panel de informacion: a la derecha del mosaico en pantalla grande,
         debajo en celular -->
    <div class="info">
      <div class="lugar-nombre">{{ site.name }}</div>

      <div class="lugar-ubicacion">
        <v-icon
          size="14"
          color="grey"
        >
          mdi-map-marker
        </v-icon>
        <span>{{ ubicacion }}</span>
      </div>

      <div class="lugar-chips">
        <v-chip
          v-for="cat in site.categories"
          :key="cat.idCategory"
          size="x-small"
          color="green"
          variant="tonal"
        >
          {{ cat.name }}
        </v-chip>
        <v-chip
          v-if="site.freeEntry"
          size="x-small"
          color="blue"
          variant="tonal"
        >
          Entrada libre
        </v-chip>
      </div>

      <p
        v-if="site.description"
        class="lugar-descripcion"
      >
        {{ site.description }}
      </p>

      <div
        v-if="site.schedule"
        class="lugar-horario"
      >
        <v-icon
          size="14"
          color="grey"
        >
          mdi-clock-outline
        </v-icon>
        <span>{{ site.schedule }}</span>
      </div>
    </div>

    <!-- Zona de acciones: le da un borde derecho a la fila aunque el lugar
         no tenga descripcion ni horario cargados -->
    <div class="acciones">
      <div class="vistas">
        <v-icon
          size="14"
          color="grey"
        >
          mdi-eye-outline
        </v-icon>
        <span>{{ site.views || 0 }}</span>
      </div>
      <v-btn
        color="primary"
        variant="tonal"
        size="small"
        class="btn-ver"
      >
        Ver lugar
        <v-icon end size="16">mdi-chevron-right</v-icon>
      </v-btn>
    </div>
  </v-card>
</template>

<script>
import apisSites from "@/apis/apisSites";

/** Cuantas fotos entran en el mosaico: una grande y dos pequenas. */
const FOTOS_EN_MOSAICO = 3;

export default {
  name: "SiteCard",
  props: {
    site: { type: Object, required: true },
  },
  emits: ["click"],
  data() {
    return { imagenes: [] };
  },
  computed: {
    /** Las que acompanan a la principal, maximo dos. */
    secundarias() {
      return this.imagenes.slice(1, FOTOS_EN_MOSAICO);
    },
    /** Se muestra como +N encima de la ultima celda. */
    fotosExtra() {
      return Math.max(0, this.imagenes.length - FOTOS_EN_MOSAICO);
    },
    ubicacion() {
      return (
        [this.site.city, this.site.department].filter(Boolean).join(", ") ||
        this.site.country ||
        "Sin ubicación"
      );
    },
  },
  async mounted() {
    try {
      const imgs = await apisSites.getImages(this.site.idSite);
      this.imagenes = imgs.map((img) => img.pathBase64);
    } catch {
      this.imagenes = [];
    }
  },
};
</script>

<style scoped>
/* Tarjeta de fila completa: en celular se apila (fotos arriba, info abajo)
   y en pantalla grande el mosaico va a la izquierda con ancho fijo */
.site-card {
  cursor: pointer;
  display: grid;
  grid-template-columns: 1fr;
}

/* El mosaico manda la proporcion; las filas en 1fr reparten el alto */
.mosaico {
  display: grid;
  gap: 3px;
  aspect-ratio: 16 / 10;
  background-color: #eceff3;
}
/* Una sola foto: ocupa todo, sin cuadricula que delate el hueco */
.mosaico--1 {
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
}
.mosaico--2 {
  grid-template-columns: 2fr 1fr;
  grid-template-rows: 1fr;
}
.mosaico--3 {
  grid-template-columns: 2fr 1fr;
  grid-template-rows: 1fr 1fr;
}
.mosaico--3 .principal {
  grid-row: 1 / 3;
}

/* min-height 0: sin esto la imagen impone su alto natural y revienta la
   fila del grid, igual que pasa dentro de un flex en columna */
.celda {
  position: relative;
  min-height: 0;
  overflow: hidden;
}
.foto {
  width: 100%;
  height: 100%;
}

.mas-fotos {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.45);
  color: #fff;
  font-weight: 700;
  font-size: 1.05rem;
}

.sin-imagen {
  aspect-ratio: 16 / 10;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
}

.info {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 14px 16px;
  min-width: 0;
}

.lugar-nombre {
  font-weight: 700;
  font-size: 1.1rem;
  color: #111;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lugar-ubicacion,
.lugar-horario {
  font-size: 0.82rem;
  color: #777;
  display: flex;
  align-items: center;
  gap: 3px;
  min-width: 0;
}
.lugar-ubicacion span,
.lugar-horario span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lugar-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.lugar-descripcion {
  font-size: 0.86rem;
  line-height: 1.45;
  color: #555;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.acciones {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 0 16px 14px;
}
.vistas {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 0.8rem;
  color: #888;
}

@media (min-width: 700px) {
  .site-card {
    grid-template-columns: 320px 1fr auto;
    align-items: stretch;
  }
  /* El mosaico deja de mandar el alto: lo iguala la fila entera */
  .mosaico,
  .sin-imagen {
    aspect-ratio: auto;
    height: 100%;
    min-height: 200px;
  }
  .info {
    padding: 16px 20px;
    justify-content: center;
  }
  .lugar-nombre {
    font-size: 1.2rem;
  }
  .acciones {
    flex-direction: column;
    align-items: flex-end;
    justify-content: center;
    gap: 12px;
    padding: 16px 20px 16px 0;
    border-left: 1px solid #eef1f5;
    margin-left: 8px;
    padding-left: 20px;
  }
}
</style>
